import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { validators } from 'investira.sdk';
import { ASSINATURA_ESTAGIO, ASSINATURA_TIPO } from '../enums';

const xTipoAssinatura = ASSINATURA_TIPO;
const xEstagioAssinatura = ASSINATURA_ESTAGIO;
// Default de 3 níveis: RG + VR + AP = 133
const ASSINATURA_NIVEIS_DEFAULT = 133;

const xTiposAssinaturaList = Object.values(xTipoAssinatura);

const getTiposAssinaturaDisponiveis = pAssinaturaNiveis => {
  return Object.values(xTipoAssinatura)
    .filter(pItem => (pAssinaturaNiveis & pItem.valor) === pItem.valor)
    .map(pItem => pItem.descricao);
};

const getEstagiosAssinaturaDisponiveis = pAssinaturaNiveis => {
  return Object.values(xEstagioAssinatura).filter(pItem => {
    const xTipo = xTipoAssinatura[pItem.id];

    return xTipo && (pAssinaturaNiveis & xTipo.valor) === xTipo.valor;
  });
};

// Chaves de regras de assinatura
const xAssinaturasKeys = [
  'RLENQ', // Enquadramento
  'RLVAR', // Risco
  'RLDUR', // Duration
  'RLSTT', // Stress Test
  'RLCNC', // Conferência Custodiante
  'RLBCK',
  'RLCRL',
  'RLFEF'
];

function hasFlag(xValor, xFlag) {
  return (xValor & xFlag) === xFlag;
}

function decodeAssinatura(xValor) {
  return {
    // Regra de negócio: todo usuário é sempre registrador
    RG: true,
    CN: hasFlag(xValor, xTipoAssinatura.CN.valor),
    VR: hasFlag(xValor, xTipoAssinatura.VR.valor),
    AP: hasFlag(xValor, xTipoAssinatura.AP.valor)
  };
}

// TODO: Implementar função de request do estágio da assiantura do recurso

/**
 * Recupera as permissões de assinatura do usuário na entidade ativa.
 *
 * @description
 * Este hook realiza as seguintes operações:
 * 1. Lê o usuário atual e a entidade selecionada na store Redux
 * 2. Resolve os níveis de assinatura disponíveis para a entidade ativa
 * 3. Identifica os valores de assinatura do membro atual
 * 4. Expõe utilitários para validar assinaturas por valor ou por chave de acesso
 *
 * @returns {Object} API com dados de assinatura e funções utilitárias
 */
function useAssinatura() {
  const { usuario_id: xUsuarioId } = useSelector(store => store.user);

  // Pega data e current da entidade do store redux
  const { data: xData, current: xCurrent } = useSelector(store => store.entidade);

  // Busca a entidade ativa
  const xEntidade = Array.isArray(xData)
    ? xData.find(xItem => xItem.entidade_id === xCurrent?.default_entidade_id)
    : null;

  const xAssinaturaNiveis = xEntidade?.config?.assinatura_niveis || ASSINATURA_NIVEIS_DEFAULT;
  const xTiposAssinaturaDisponiveis = useMemo(
    () => getTiposAssinaturaDisponiveis(xAssinaturaNiveis),
    [xAssinaturaNiveis]
  );
  const xEstagiosAssinaturaDisponiveis = useMemo(
    () => getEstagiosAssinaturaDisponiveis(xAssinaturaNiveis),
    [xAssinaturaNiveis]
  );

  const xMembroAtual = Array.isArray(xCurrent?.membros)
    ? xCurrent.membros.find(xMembro => xMembro.usuario_id === xUsuarioId)
    : null;

  // Assinaturas atuais do usuário na entidade ativa
  const xCurrentAssinaturas = xMembroAtual?.acessos
    ? { ...(xEntidade?.assinaturas || {}) }
    : xAssinaturasKeys.reduce((xAcc, xKey) => ({ ...xAcc, [xKey]: null }), {});

  /**
   * Resolve o valor numérico de assinatura associado a uma chave de acesso.
   *
   * @param {string} pAcessoKey Chave do recurso a ser consultado
   * @returns {number | null} Valor numérico da assinatura ou `null` quando inválido
   */
  const getValorAssinaturaPorAcessoKey = pAcessoKey => {
    const xValorRaw = xCurrentAssinaturas[pAcessoKey];

    if (!pAcessoKey || validators.isEmpty(xValorRaw) || validators.isNull(xValorRaw)) return null;

    const xValor = Number(xValorRaw);
    if (Number.isNaN(xValor)) return null;

    return xValor;
  };

  /**
   * Normaliza um valor bruto de assinatura para número.
   *
   * @param {string | number | null | undefined} pValorAssinatura Valor bruto de assinatura
   * @returns {number | null} Valor numérico normalizado ou `null` quando inválido
   */
  const normalizeValorAssinatura = pValorAssinatura => {
    if (validators.isEmpty(pValorAssinatura) || validators.isNull(pValorAssinatura)) return null;
    const xValor = Number(pValorAssinatura);
    if (Number.isNaN(xValor)) return null;
    return xValor;
  };

  // Checa assinatura por valor direto (ex.: 3, 131)
  /**
   * Verifica se um valor de assinatura possui a permissão informada.
   *
   * @param {string | number | null | undefined} pValorAssinatura Valor de assinatura a ser validado
   * @param {string | null} [pAssinaturaId='AP'] Identificador da permissão desejada ou `null` para qualquer assinatura ativa
   * @returns {boolean} `true` quando a permissão está presente
   */
  const canAssinar = (pValorAssinatura, pAssinaturaId = 'AP') => {
    if (pAssinaturaId === 'RG') return true;

    const xValor = normalizeValorAssinatura(pValorAssinatura);
    if (validators.isNull(xValor)) return false;

    if (pAssinaturaId === null) return xValor > 0;

    const xAssinatura = xTipoAssinatura[pAssinaturaId];
    if (!xAssinatura) return false;

    return hasFlag(xValor, xAssinatura.valor);
  };

  // Compatibilidade: checa assinatura via acessoKey (lendo do store)
  /**
   * Verifica permissões de assinatura a partir da chave de acesso do recurso.
   *
   * @param {string} pAcessoKey Chave do recurso a ser consultado
   * @param {string | null} [pAssinaturaId='AP'] Identificador da permissão desejada ou `null` para qualquer assinatura ativa
   * @returns {boolean} `true` quando a permissão está presente para a chave informada
   */
  const canAssinarPorAcessoKey = (pAcessoKey, pAssinaturaId = 'AP') => {
    const xValor = getValorAssinaturaPorAcessoKey(pAcessoKey);
    return canAssinar(xValor, pAssinaturaId);
  };

  // Compatibilidade com API antiga
  const canAcessar = canAssinarPorAcessoKey;

  // Retorna todos os acessos atuais
  // 149 -> AP, VR, CN, RG
  // 148 -> VR, CN, RG
  // 145 -> AP, CN, RG
  // 133 -> AP, VR, RG
  // 144 -> CN, RG
  // 132 -> VR, RG
  // 129 -> AP, RG
  /**
   * Retorna uma cópia das assinaturas atuais da entidade ativa.
   *
   * @returns {Object} Mapa de assinaturas por chave de recurso
   */
  const getAssinaturas = () => ({ ...xCurrentAssinaturas });

  // Retorna todas as permissões de assinatura ativas para uma chave
  /**
   * Decodifica um valor de assinatura em flags por tipo.
   *
   * @param {string | number | null | undefined} pValorAssinatura Valor de assinatura a ser decodificado
   * @returns {Object} Objeto com flags booleanas para `RG`, `CN`, `VR` e `AP`
   */
  const getAssinaturasTipo = pValorAssinatura => {
    const xValor = normalizeValorAssinatura(pValorAssinatura);
    if (validators.isNull(xValor)) return decodeAssinatura(0);

    return decodeAssinatura(xValor);
  };

  // const getAssintaturaId = pValor => {
  //   return Object.values(ASSINATURA_TIPO).find(xTipo => xTipo.valor === pValor);
  // };

  // Retorna true se houver ao menos uma chave com a assinatura informada
  /**
   * Verifica se existe ao menos um recurso com a assinatura informada.
   *
   * @param {string} pAssinaturaId Identificador da assinatura a ser procurada
   * @returns {boolean} `true` quando algum recurso contém a assinatura informada
   */
  const hasAlgumaAssinatura = pAssinaturaId => {
    return xAssinaturasKeys.some(xKey => canAssinarPorAcessoKey(xKey, pAssinaturaId));
  };

  const podeRegistrar = pValorAssinatura => canAssinar(pValorAssinatura, 'RG');
  const podeConferir = pValorAssinatura => canAssinar(pValorAssinatura, 'CN');
  const podeVerificar = pValorAssinatura => canAssinar(pValorAssinatura, 'VR');
  const podeAprovar = pValorAssinatura => canAssinar(pValorAssinatura, 'AP');

  // Combinações explícitas com RG (sempre true por regra de negócio)
  const podeRegistrarVerificarAprovar = pValorAssinatura =>
    podeRegistrar(pValorAssinatura) &&
    podeVerificar(pValorAssinatura) &&
    podeAprovar(pValorAssinatura);

  const podeRegistrarConferirVerificar = pValorAssinatura =>
    podeRegistrar(pValorAssinatura) &&
    podeConferir(pValorAssinatura) &&
    podeVerificar(pValorAssinatura);

  const podeRegistrarConferirAprovar = pValorAssinatura =>
    podeRegistrar(pValorAssinatura) &&
    podeConferir(pValorAssinatura) &&
    podeAprovar(pValorAssinatura);

  const podeRegistrarConferirVerificarAprovar = pValorAssinatura =>
    podeRegistrar(pValorAssinatura) &&
    podeConferir(pValorAssinatura) &&
    podeVerificar(pValorAssinatura) &&
    podeAprovar(pValorAssinatura);

  return {
    usuario_id: xUsuarioId,
    entidade: xEntidade,
    assinaturaNiveis: xAssinaturaNiveis,
    tiposAssinatura: xTiposAssinaturaList,
    tiposAssinaturaDisponiveis: xTiposAssinaturaDisponiveis,
    estagiosAssinaturaDisponiveis: xEstagiosAssinaturaDisponiveis,
    assinaturaMap: xTipoAssinatura,
    acessosKeys: xAssinaturasKeys,
    getAssinaturas,
    getAssinaturasTipo,
    // getAssintaturaId,
    canAssinar,
    canAssinarPorAcessoKey,
    canAcessar,
    hasAlgumaAssinatura,
    podeRegistrar,
    podeConferir,
    podeVerificar,
    podeAprovar,
    podeRegistrarVerificarAprovar,
    podeRegistrarConferirVerificar,
    podeRegistrarConferirAprovar,
    podeRegistrarConferirVerificarAprovar
  };
}

export default useAssinatura;
